import { useState, useRef, useEffect } from "react";
import { Share2, Facebook, Instagram, Link2, Check, Mail, Twitter } from "lucide-react";
import { toast } from "sonner";

// Share menu with Facebook (share dialog), Instagram (copy link — IG web has no
// direct URL share), and a copy / native-share fallback.
// `path` = the public frontend page URL humans open; `ogPath` (optional) = the
// server-rendered OG preview URL given to social crawlers (Facebook).
export const SocialShare = ({ path, ogPath, title = "", testid = "social-share", label = "Share", iconOnly = false, dark = false }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const toAbs = (p) => (p?.startsWith("http") ? p : `${window.location.origin}${p || ""}`);
  const url = toAbs(path);
  const ogUrl = toAbs(ogPath || path);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const copyLink = async (msg) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(msg || "Link copied — share it anywhere");
    setTimeout(() => setCopied(false), 1800);
  };

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogUrl)}`,
      "_blank", "noopener,noreferrer,width=620,height=640");
    setOpen(false);
  };
  const shareInstagram = async () => {
    await copyLink("Link copied — paste it into your Instagram story or bio ✨");
    setOpen(false);
  };
  const shareX = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(ogUrl)}&text=${encodeURIComponent(title)}`,
      "_blank", "noopener,noreferrer,width=620,height=540");
    setOpen(false);
  };
  const shareEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(title || "Something worth a look")}&body=${encodeURIComponent(`I thought you'd like this:\n\n${url}`)}`;
    setOpen(false);
  };
  const copyOrNative = async () => {
    try {
      if (navigator.share) { await navigator.share({ title, url }); }
      else { await copyLink(); }
    } catch { /* user cancelled */ }
    setOpen(false);
  };

  const Item = ({ onClick, icon: Icon, children, tid }) => (
    <button type="button" onClick={onClick} data-testid={tid}
      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-[#1A242B] hover:bg-[#144F75]/[0.06] transition-colors">
      <Icon size={15} className="text-[#144F75]" /> {children}
    </button>
  );

  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-btn`} title="Share"
        className={`inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors ${dark ? "border-white/40 text-white hover:bg-white/10" : "border-[#144F75]/20 text-[#144F75] hover:bg-[#144F75]/[0.06]"} ${iconOnly ? "p-2" : "px-3 py-2 text-xs"}`}>
        <Share2 size={iconOnly ? 15 : 13} /> {!iconOnly && label}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-52 bg-white rounded-xl shadow-lg border border-[#E5E0D8] overflow-hidden" data-testid={`${testid}-menu`}>
          <Item onClick={shareFacebook} icon={Facebook} tid={`${testid}-facebook`}>Share to Facebook</Item>
          <Item onClick={shareInstagram} icon={Instagram} tid={`${testid}-instagram`}>Share to Instagram</Item>
          <Item onClick={shareX} icon={Twitter} tid={`${testid}-x`}>Share to X</Item>
          <Item onClick={shareEmail} icon={Mail} tid={`${testid}-email`}>Share by email</Item>
          <Item onClick={copyOrNative} icon={copied ? Check : Link2} tid={`${testid}-copy`}>{copied ? "Link copied" : "Copy link"}</Item>
        </div>
      )}
    </div>
  );
};
